import SearchIcon from '@mui/icons-material/Search';
import { TextField, Theme } from '@mui/material';
import { createStyles, withStyles, WithStyles } from "@mui/styles";
import * as api from "api/Core";
import BoxDrawer from 'components/BoxDrawer';
import IconButtonCancel from 'components/Buttons/IconButtonCancel';
import { SpinnerIndicator } from 'components/CallServer';
import { GridEx } from 'components/MuiExtras/Grid/GridEx';
import MuiContainer from 'components/MuiExtras/MuiContainer';
import MuiLink from 'components/MuiExtras/MuiLink';
import MuiPagination from 'components/MuiExtras/MuiPagination';
import MuiPaper from 'components/MuiExtras/MuiPaper';
import MuiMuted from 'components/MuiExtras/Typography/MuiMuted';
import { Formaters } from 'lib/Formarters';
import { PublicationType } from 'models/ApiEnums';
import { DashboardPublicationModel, DashboardUserAndGroupsFilter, DashboardUserAndGroupsParticipationPublicationDetailColumn, DashboardUserAndGroupsParticipationSummaryColumn, PagedResult } from 'models/ApiModels';
import * as React from 'react';
import { WithTranslation, withTranslation } from 'react-i18next';
import { getPublicationUrl } from 'views/Timeline/Components/FeedHelpers';

/////////////////////////////////////////////////////////////////////////////////////////////////////////////

const styles = (theme: Theme) => createStyles({
    table: {
        margin: 0,
        padding: 0
    },
    tableCell: {
        paddingBottom: theme.spacing(2)
    }
});

/////////////////////////////////////////////////////////////////////////////////////////////////////////////

interface IState {
    ds: PagedResult<DashboardPublicationModel>;
    offset: number;
    loading: boolean;
    searchText: string;
}

interface IProps {
    subtitle: string;
    columnToSummary: DashboardUserAndGroupsParticipationSummaryColumn;
    detailColumn: DashboardUserAndGroupsParticipationPublicationDetailColumn;
    startDate: Date;
    endDate: Date;
    filter: DashboardUserAndGroupsFilter;
    drilldownId: string;
    onClose: () => void;
}

type Props = IProps & WithStyles<typeof styles> & WithTranslation;

const rowsPerPage = 10;

/////////////////////////////////////////////////////////////////////////////////////////////////////////////

class ParticipationPublicationDrawer extends React.PureComponent<Props, IState> {

    private searchRef: any = null;

    constructor(props: Props) {
        super(props);
        this.state = {
            ds: null,
            offset: 0,
            loading: true,
            searchText: ""
        };
    }

    ///
    private getData(offset: number) {

        api.getDashboardUserAndGroupsParticipationPublication({
            columnToSummary: this.props.columnToSummary,
            detailColumn: this.props.detailColumn,
            startDate: this.props.startDate,
            endDate: this.props.endDate,
            ...this.props.filter,
            drillDownFilter: {
                id: this.props.drilldownId,
                title: this.searchRef ? this.searchRef.value : null,
            },
            range: { offset: offset, take: rowsPerPage },
        })
            .then((result: PagedResult<DashboardPublicationModel>) => {

                this.setState({ ds: result, loading: false, offset: offset });
            })
            .catch((error) => {
                this.setState({ ds: null, loading: false, offset: 0 });
            });
    }

    ///
    private handleSearch(e: React.KeyboardEvent<HTMLDivElement>) {
        if (e.keyCode == 13) this.getData(0);
    }

    ///
    private renderContent() {
        const { ds, loading } = this.state;
        const { classes } = this.props;

        return (
            <GridEx container fullHeight direction="column" wrap="nowrap">
                <GridEx>

                    <MuiContainer>
                        <TextField
                            fullWidth
                            placeholder={this.props.t("Common.Filter.Adv.InputPlaceholder")}
                            value={this.state.searchText}
                            onChange={() => this.setState({ searchText: this.searchRef.value })}
                            onKeyDown={(e) => this.handleSearch(e)}
                            onBlur={() => this.getData(0)}
                            inputRef={(ref) => this.searchRef = ref}
                            InputProps={
                                {
                                    startAdornment: <SearchIcon />,
                                    endAdornment: this.state.searchText != ""
                                        ? <IconButtonCancel
                                            size="small"
                                            onClick={
                                                () => {
                                                    this.setState({ searchText: "" }, () => this.getData(0));
                                                }
                                            } />
                                        : null
                                }
                            }
                        />
                    </MuiContainer>

                </GridEx>
                <GridEx item xs overflow="auto">
                    {
                        loading
                            ? <SpinnerIndicator />
                            : (
                                <table className={classes.table}>
                                    <tbody>
                                        {
                                            ds.items.map((v, index) => {

                                                var url = getPublicationUrl(v.publicationId, v.publicationType);
                                                if (v.publicationType == PublicationType.Question
                                                    && this.props.columnToSummary == DashboardUserAndGroupsParticipationSummaryColumn.Answers
                                                    && this.props.detailColumn == DashboardUserAndGroupsParticipationPublicationDetailColumn.AuthorDrillDown) {
                                                    // Inicia filtrando respostas do usuário
                                                    url += "?afu=" + this.props.drilldownId;
                                                }

                                                return (
                                                    <tr key={index}>
                                                        <td className={classes.tableCell}>
                                                            <MuiLink color="secondary" href={url} target="_blank" >{v.title}</MuiLink>
                                                            <MuiMuted>
                                                                <small>
                                                                    <div>{Formaters.relativeTime(v.creationDate)}</div>
                                                                </small>
                                                            </MuiMuted>
                                                        </td>
                                                    </tr>
                                                );
                                            })
                                        }
                                    </tbody>
                                </table>
                            )
                    }
                </GridEx>

                {
                    !loading &&
                    <GridEx item>
                        <MuiPagination
                            limit={rowsPerPage}
                            offset={this.state.offset}
                            total={ds.total}
                            onClick={(e, offset) => this.getData(offset)}
                        />
                    </GridEx>
                }
            </GridEx>
        );
    }

    ///
    public componentDidMount() {
        this.getData(0);
    }

    ///
    public render() {
        const { columnToSummary, subtitle, onClose } = this.props;

        return (
            <BoxDrawer
                open
                overflow="hidden"
                onClose={() => onClose()}
                title={
                    this.props.t(
                        columnToSummary == DashboardUserAndGroupsParticipationSummaryColumn.Publications
                            ? "Views.Dashboards.UserAndGroups.PublicationsList.Publications"
                            : "Views.Dashboards.UserAndGroups.PublicationsList.Answers"
                    )
                }
                subtitle={subtitle}
            >
                <MuiPaper fullHeight elevation={0}>
                    {this.renderContent()}
                </MuiPaper>
            </BoxDrawer>
        );
    }
}

export default withTranslation()(withStyles(styles)(ParticipationPublicationDrawer));